import { useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import fidelityLogo from '@/assets/images/fidelity-logo.svg';
import secclLogo from '@/assets/images/seccl-logo.png';
import avivaLogo from '@/assets/images/aviva-logo.svg';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { getInfoSheet, type InfoSheetKey } from '@/content/info-sheets';
import { useOpenSupport } from '@/store/supportStore';
import { track } from '@/analytics';

type InfoSheetProps = {
  /** Which sheet to show. `null` closes it — the trigger owns the choice. */
  sheet: InfoSheetKey | null;
  onOpenChange: (open: boolean) => void;
  /** Personalises the "How does Mia work?" copy; safe to omit. */
  childFirstName?: string;
  className?: string;
};

function CloseIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#484747'
      strokeWidth='2.4'
      strokeLinecap='round'
      aria-hidden='true'
    >
      <path d='M18 6 6 18M6 6l12 12' />
    </svg>
  );
}

/**
 * The partner strip under "Your money is protected" (AC 7).
 *
 * Its own markup rather than the amount screen's `PartnerBar`: the design
 * orders this one Fidelity, Aviva, Seccl and credits Octopus in the Seccl
 * alt text, because the body copy above it has just named all three.
 */
function PartnerStrip() {
  return (
    <div className='mt-4.5 flex flex-wrap items-center justify-center gap-3.25 border-t border-[#eceae3] pt-4'>
      <img
        src={fidelityLogo}
        alt='Fidelity International'
        className='h-3.75 w-auto max-w-19.5 object-contain'
      />
      <span aria-hidden='true' className='h-3.25 w-px bg-[#e0ddd5]' />
      <img
        src={avivaLogo}
        alt='Aviva'
        className='h-3 w-auto max-w-14 object-contain'
      />
      <span aria-hidden='true' className='h-3.25 w-px bg-[#e0ddd5]' />
      <img
        src={secclLogo}
        alt='Seccl, part of Octopus'
        className='h-4.25 w-auto max-w-15.5 object-contain'
      />
    </div>
  );
}

/**
 * The information sheets behind the amount step's trust tiles and the
 * allowance warning (GIFTWEB-32 AC 6, AC 7).
 *
 * One component for all of them: they differ only in copy and whether the
 * partner strip shows, and the accessibility AC 9 and AC 10 ask for — focus
 * trap, Escape, scrim dismissal, scroll lock — is worth having in one place
 * rather than re-earned per sheet. All of it comes from the shared `Sheet`,
 * which is Radix Dialog underneath, and which already renders as a bottom
 * sheet on mobile and a centred card on desktop, exactly as the design does.
 */
function InfoSheet({
  sheet,
  onOpenChange,
  childFirstName,
  className,
}: InfoSheetProps) {
  /**
   * The sheet that was last asked for.
   *
   * The card has to keep its copy while it animates out, and the dialog has to
   * stay mounted long enough to hand focus back to whatever opened it — so
   * `sheet` turning null closes it rather than deleting it.
   */
  const [lastSheet, setLastSheet] = useState<InfoSheetKey | null>(sheet);
  if (sheet && sheet !== lastSheet) setLastSheet(sheet);

  /**
   * Whatever had focus when the sheet opened (AC 9).
   *
   * Radix hands focus back to its own `Dialog.Trigger`, and these sheets have
   * none — the tiles and the allowance warning open them by setting state, so
   * without this, dismissing drops focus on the body and a keyboard user
   * restarts from the top of the page.
   */
  const opener = useRef<HTMLElement | null>(null);

  /**
   * Set when this sheet is closing in order to hand over to support (AC 8).
   *
   * Both sheets are modal dialogs, so without this the closing one would pull
   * focus back to the button behind it at the same moment the support sheet
   * is claiming it, and whichever won would be a coin toss.
   */
  const handingOver = useRef(false);

  const openSupport = useOpenSupport();

  if (!lastSheet) return null;

  const content = getInfoSheet(lastSheet, childFirstName);

  return (
    <Sheet open={sheet !== null} onOpenChange={onOpenChange}>
      <SheetContent
        // The design puts the close beside the title rather than floating it
        // over the corner, so the shared one would be a second close button.
        showCloseButton={false}
        onOpenAutoFocus={() => {
          opener.current = document.activeElement as HTMLElement | null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (handingOver.current) {
            handingOver.current = false;
            return;
          }
          opener.current?.focus();
        }}
        data-testid={`info-sheet-${lastSheet}`}
        className={cn(
          'gap-0 bg-white px-5.5 pt-3.5 lg:max-w-115 lg:px-6.5 lg:pt-6',
          className,
        )}
      >
        <SheetHeader className='flex-row items-start gap-3 p-0'>
          <SheetTitle className='min-w-0 flex-1 text-xl font-bold tracking-[-0.01em] text-[#1a201d]'>
            {content.title}
          </SheetTitle>
          <button
            type='button'
            aria-label={`Close ${content.title}`}
            onClick={() => onOpenChange(false)}
            className='flex size-9.5 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[#ebebea]'
          >
            <CloseIcon />
          </button>
        </SheetHeader>

        <SheetDescription asChild>
          <div className='mt-3 flex flex-col gap-3.5'>
            {content.sections.map((section) => (
              <div key={section.head ?? section.body}>
                {section.head && (
                  <div className='mb-0.5 text-[14.5px] font-bold text-[#1a201d]'>
                    {section.head}
                  </div>
                )}
                <div className='text-sm leading-[1.6] text-[#4a534d]'>
                  {section.body}
                </div>
              </div>
            ))}
          </div>
        </SheetDescription>

        {content.partners && <PartnerStrip />}

        <button
          type='button'
          onClick={() => onOpenChange(false)}
          className='mt-5 h-12.5 w-full cursor-pointer rounded-[13px] border-none bg-[#256956] text-[15px] font-bold text-white'
        >
          Got it
        </button>

        {content.support && (
          <button
            type='button'
            aria-haspopup='dialog'
            onClick={() => {
              handingOver.current = true;
              track('supportOpened', { from: 'allowance' });
              // One update, so the sheets swap rather than stack.
              openSupport();
              onOpenChange(false);
            }}
            className='mt-2.25 h-12 w-full cursor-pointer rounded-[13px] border-[1.5px] border-[#cfe0d8] bg-transparent text-[14.5px] font-bold text-[#256956]'
          >
            Need help?
          </button>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default InfoSheet;
